// @flow
/** @jsx h */
import { h, Fragment } from 'preact';
import { styled } from 'goober';
import Modal from './Modal';
import CustomButton from './CustomButton';
import { useEffect, useState } from 'preact/hooks';
import Checkbox from 'preact-material-components/Checkbox';
import 'preact-material-components/Checkbox/style.css';
import {
  useCheckoutContext,
  useCheckoutDispatchContext,
} from '../../storeProviders/checkoutWidgetStore';
import { cancelOrderAPI } from '../../api/controllers/checkoutController';
import { useRequest } from '../../api/services/useRequest';
import type { Response } from '../../api/services/useRequest';
import type {
  CancelOrderResponseType,
  ErrorResponseType,
} from '../../api/controllers/checkoutController';
import useCheckoutErrorModal from './hooks/useCheckoutError';
import { useCheckoutTracker } from './tracker';

const CancelContainer = styled('div')`
  .buttonsContainer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 250px;
    margin: 0px auto;
  }
  .reasonButtonsContainer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 250px;
    margin: 20px auto 40px;
  }
  .infoIcon {
    width: fit-content;
    color: orange;
    margin: 0px auto 10px;
    font-size: 30px;
  }
  .cancelText {
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    color: var(--primaryCTAColor);
    margin: 0 auto 10px;
    max-width: 250px;
  }
  .confirmButton {
    background-color: #d9d9d9 !important;
    color: #000000 !important;
    width: 110px !important;
    height: 20px !important;
    border-radius: 5px !important;
    font-size: 18px !important;
  }
  .cancelButton {
    width: 110px !important;
    height: 20px !important;
    border-radius: 5px !important;
    font-size: 18px !important;
  }
  .checkBoxContainer {
    display: flex;
    align-items: flex-start;
    padding: 5px 0;
  }
  .reasonHeader {
    font-weight: 600;
    font-size: 20px;
    line-height: 1.5;
    color: var(--primaryCTAColor);
    max-width: 265px;
    margin: 50px auto 10px;
  }
  .reasonText {
    padding-top: 9px;
    line-height: 1.5;
  }
  .modalBox {
    max-width: 395px;
    width: 95%;
    background-color: #ffffff;
    padding: 30px 10px;
    line-height: 0;
    border-radius: 15px !important;
    animation: openModal 0.2s ease-out forwards;
    @keyframes openModal {
      0% {
        transform: scale(0.5);
      }
      100% {
        transform: scale(i);
      }
    }
  }
  .outerModalClass {
    top: 140px !important;
    transform: translate(-50%, 0%) !important;
  }
`;

const CancelModal = (props) => {
  const pageKey = 'ec_pv_cancel_alert';
  const cancellationReasons = [
    'Don’t want to purchase anymore',
    'Not comfortable sharing personal details',
    'OTP not received',
    'Will use other payment methods',
    'Others',
  ];
  const [selectedReason, setSelectedReason] = useState('');
  const [showReasonsScreen, setShowReasonScreen] = useState(false);
  const checkoutState = useCheckoutContext();
  const checkoutDispatch = useCheckoutDispatchContext();
  const checkoutTracker = useCheckoutTracker();

  const cancelOrderRequest: Response<
    any,
    CancelOrderResponseType,
    ErrorResponseType,
  > = useRequest();

  const { setCheckoutErrorModal } = useCheckoutErrorModal();

  const cancelTransaction = () => {
    cancelOrderAPI(cancelOrderRequest.makeRequest);
  };

  const sendTransactionComplete = (callbackData) => {
    checkoutDispatch({
      type: 'SEND_MESSAGE',
      payload: {
        payload: callbackData && callbackData.payload,
        signature: callbackData && callbackData.signature,
        actionName: 'onTransactionComplete',
      },
    });
  };

  useEffect(() => {
    if (cancelOrderRequest.value) {
      sendTransactionComplete(cancelOrderRequest.value.callbackData);
    } else if (cancelOrderRequest.error) {
      sendTransactionComplete(cancelOrderRequest.error.callbackData);
    }
  }, [cancelOrderRequest.value, cancelOrderRequest.error]);

  useEffect(() => {
    if (checkoutState.closeIconConfig.showCancellationPopup)
      checkoutTracker.trackPageView(pageKey);
  }, [
    checkoutState.closeIconConfig &&
      checkoutState.closeIconConfig.showCancellationPopup,
  ]);

  useEffect(() => {
    if (showReasonsScreen) {
      checkoutTracker.trackPageView('ec_pv_cancel_reasons');
    }
  }, [showReasonsScreen]);

  return (
    checkoutState.closeIconConfig.showCancellationPopup && (
      <CancelContainer>
        <Modal
          open
          className='modalBox'
          outerModalClass={showReasonsScreen ? '' : 'outerModalClass'}
          withCrossIcon={false}
          disableBackdropClick={true}>
          {showReasonsScreen ? (
            <Fragment>
              <div className='reasonHeader'>
                Why do you want to cancel the payment?
              </div>
              {cancellationReasons.map((reason, index) => {
                return (
                  <div className='checkBoxContainer'>
                    <Checkbox
                      id={`basic-checkbox-${index}`}
                      ref={(cb) => {
                        this.cb = cb;
                      }}
                      checked={selectedReason === reason}
                      onChange={() => {
                        setSelectedReason(reason);
                      }}
                    />
                    <label
                      for={`basic-checkbox-${index}`}
                      id={`basic-checkbox-label-${index}`}
                      className='reasonText'>
                      {reason}
                    </label>
                  </div>
                );
              })}
              <div className='reasonButtonsContainer'>
                <CustomButton
                  name='Cancel'
                  className='confirmButton'
                  onClick={() => {
                    checkoutDispatch({
                      type: 'STORE_STATE',
                      payload: {
                        closeIconConfig: {
                          ...checkoutState.closeIconConfig,
                          showCancellationPopup: false,
                        },
                      },
                    });
                  }}
                />
                <CustomButton
                  name='Confirm'
                  className='cancelButton'
                  onClick={cancelTransaction}
                  track={(e) => {
                    checkoutTracker.trackInputEntered(
                      'ec_input_cancel_reason',
                      pageKey,
                      {
                        button_label: e.target.name,
                        selected_cancel_reason: selectedReason,
                      },
                    );
                  }}
                />
              </div>
            </Fragment>
          ) : (
            <Fragment>
              <div class='material-icons infoIcon'>info</div>
              <div className='cancelText'>
                Are you sure you want to cancel the payment?
              </div>
              <div className='buttonsContainer'>
                <CustomButton
                  name='Yes'
                  className='confirmButton'
                  onClick={() => {
                    setShowReasonScreen(true);
                  }}
                  track={(e) => {
                    checkoutTracker.trackInputEntered(
                      'ec_input_cancel_confirm',
                      pageKey,
                      {
                        button_label: e.target.name,
                      },
                    );
                  }}
                />
                <CustomButton
                  name='No'
                  className='cancelButton'
                  onClick={() => {
                    checkoutDispatch({
                      type: 'STORE_STATE',
                      payload: {
                        closeIconConfig: {
                          ...checkoutState.closeIconConfig,
                          showCancellationPopup: false,
                        },
                      },
                    });
                  }}
                />
              </div>
            </Fragment>
          )}
        </Modal>
      </CancelContainer>
    )
  );
};
export default CancelModal;



// WEBPACK FOOTER //
// ./components/checkoutWidget/CancelModal.js